import React from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  FormGroup,
  Input,
  Label,
  Spinner,
} from 'reactstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { FiPlus, FiArrowLeft, FiMusic, FiFolder } from 'react-icons/fi';
import {
  Practice,
  PracticeModule,
  PitchMap,
  ModuleVariant,
  PitchTempoOption,
  PRACTICE_LEVELS,
  PRACTICE_TYPES,
} from './practice/practiceTypes';
import {
  createPractice,
  getCategories,
  getSubCategories,
  getPracticeModules,
  getModuleVariants,
  getRecordingUrl,
} from './practice/practiceApi';

// A module variant ready to edit (with its streaming audio URL resolved).
type EditorVariant = ModuleVariant & { audioUrl?: string };
const variantLabel = (v: ModuleVariant) =>
  `${v.aiRecordingType}${v.recordingType ? ` · ${v.recordingType}` : ''}`;
const pitchTempoValue = (option: PitchTempoOption) => `${option.pitchKey}__${option.tempo}`;
const pitchTempoLabel = (option: PitchTempoOption) =>
  `Pitch ${option.pitchKey} · ${option.tempo} BPM`;
const courseLabel = (course: any) =>
  Array.isArray(course?.course_name) ? course.course_name.join(', ') : String(course?.course_name ?? '');

const audioDownloadName = (moduleName: string, variant?: EditorVariant | null) => {
  const slug = (value: string) =>
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  const sourceName = variant?.recordingPath?.split('/').pop() || '';
  const extensionMatch = sourceName.match(/(\.[a-z0-9]+)$/i);
  const extension = extensionMatch?.[1]?.toLowerCase() || '';
  const variantName = slug(variant?.aiRecordingType || '');

  return `${slug(moduleName)}${variantName ? `_${variantName}` : ''}${extension}`;
};

import ModuleUpload from './practice/ModuleUpload';
import PitchMapEditor from './practice/PitchMapEditor';
import './practice/practice.scss';

type Props = {
  courses: any[];
};

type ReturnView =
  | { name: 'subcategories'; category: Practice }
  | { name: 'modules'; practice: Practice; category?: Practice };

type View =
  | { name: 'categories' }
  | { name: 'subcategories'; category: Practice }
  | { name: 'modules'; practice: Practice; category?: Practice }
  | { name: 'upload'; practice: Practice; category?: Practice }
  | {
      name: 'editor';
      practice: Practice;
      category?: Practice;
      moduleId: string | number;
      title: string;
      isAIRecording: boolean;
      aiVerified: boolean;
      variants: EditorVariant[];
      activeVariantId: string | number | null;
      pitchTempoOptions: PitchTempoOption[];
      selectedPitchTempo: PitchTempoOption | null;
      loadingPitchTempo: boolean;
      returnView?: ReturnView;
    };

const courseId = (c: any) => c?.course_id ?? c?.id;

const PracticeMaterial: React.FC<Props> = ({ courses }) => {
  const [view, setView] = React.useState<View>({ name: 'categories' });

  const [categories, setCategories] = React.useState<Practice[]>([]);
  const [loading, setLoading] = React.useState(true);

  const [subCategories, setSubCategories] = React.useState<Practice[]>([]);
  const [directModules, setDirectModules] = React.useState<PracticeModule[]>([]);
  const [directModulesLoading, setDirectModulesLoading] = React.useState(false);

  const [modules, setModules] = React.useState<PracticeModule[]>([]);
  const [modulesLoading, setModulesLoading] = React.useState(false);
  const [modulesLoadingMore, setModulesLoadingMore] = React.useState(false);
  const [modulesHasMore, setModulesHasMore] = React.useState(false);
  const [modulesPage, setModulesPage] = React.useState(1);
  const [directModulesPage, setDirectModulesPage] = React.useState(1);
  const [directModulesHasMore, setDirectModulesHasMore] = React.useState(false);
  const [directModulesLoadingMore, setDirectModulesLoadingMore] = React.useState(false);
  const [openingId, setOpeningId] = React.useState<string | number | null>(null);
  const modulesSentinelRef = React.useRef<HTMLDivElement | null>(null);
  const directModulesSentinelRef = React.useRef<HTMLDivElement | null>(null);
  const modulesGenRef = React.useRef(0);
  const directModulesGenRef = React.useRef(0);
  const modulesLoadingMoreRef = React.useRef(false);
  const directModulesLoadingMoreRef = React.useRef(false);
  const [selectedCourseId, setSelectedCourseId] = React.useState<string>('');

  // Add category / sub-category modal
  const [modalOpen, setModalOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [taggedCourses, setTaggedCourses] = React.useState<any[]>([]);
  const [taggedLevels, setTaggedLevels] = React.useState<any[]>([]);
  const [practiceType, setPracticeType] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  // Where the new folder goes; null parent = top-level category.
  const [createCtx, setCreateCtx] = React.useState<{
    parentFolderId: number | null;
    basePath: string;
    label: string;
  }>({ parentFolderId: null, basePath: '/practice', label: 'Category' });

  const createdBy = localStorage.getItem('userId') || '';
  const courseOptions = courses
    .map((c) => ({ value: courseId(c), label: courseLabel(c) }))
    .filter((o) => o.value != null && o.label);
  const levelOptions = PRACTICE_LEVELS.map((l) => ({ value: l, label: l }));

  const loadCategories = async (courseIdValue = selectedCourseId) => {
    setLoading(true);
    try {
      setCategories(await getCategories(courseIdValue || undefined));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load categories.');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadCategories();
  }, [selectedCourseId]);

  const mergeModules = (current: PracticeModule[], incoming: PracticeModule[]) => {
    const seen = new Set(current.map((m) => String(m.id)));
    return [...current, ...incoming.filter((m) => !seen.has(String(m.id)))];
  };

  const loadModules = async (
    folderId: string | number,
    courseIdValue = selectedCourseId,
    page = 1
  ) => {
    const gen = page === 1 ? ++modulesGenRef.current : modulesGenRef.current;
    if (page === 1) {
      setModulesLoading(true);
      setModulesHasMore(false);
      setModulesPage(1);
    } else {
      if (modulesLoadingMoreRef.current) return;
      modulesLoadingMoreRef.current = true;
      setModulesLoadingMore(true);
    }
    try {
      const result = await getPracticeModules(folderId, courseIdValue || undefined, page);
      if (gen !== modulesGenRef.current) return;
      setModules((prev) => (page === 1 ? result.modules : mergeModules(prev, result.modules)));
      setModulesPage(result.page);
      setModulesHasMore(result.hasMore);
    } catch (e) {
      console.error(e);
      if (gen === modulesGenRef.current) toast.error('Failed to load modules.');
    } finally {
      if (page === 1) {
        if (gen === modulesGenRef.current) setModulesLoading(false);
      } else {
        modulesLoadingMoreRef.current = false;
        if (gen === modulesGenRef.current) setModulesLoadingMore(false);
      }
    }
  };

  React.useEffect(() => {
    if (view.name === 'modules') loadModules(view.practice.id);
  }, [view, selectedCourseId]);

  React.useEffect(() => {
    const el = modulesSentinelRef.current;
    if (!el || view.name !== 'modules' || !modulesHasMore || modulesLoading) return;
    const folderId = view.practice.id;
    const courseIdValue = selectedCourseId;
    const nextPage = modulesPage + 1;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadModules(folderId, courseIdValue, nextPage);
        }
      },
      { root: null, rootMargin: '240px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [view, selectedCourseId, modulesHasMore, modulesLoading, modulesPage]);

  const handleCourseFilterChange = (courseIdValue: string) => {
    setSelectedCourseId(courseIdValue);
    setSubCategories([]);
    setDirectModules([]);
    setDirectModulesHasMore(false);
    setDirectModulesPage(1);
    setModules([]);
    setModulesHasMore(false);
    setModulesPage(1);
    setView({ name: 'categories' });
  };

  const renderCourseFilter = () => (
    <div className="practice__filter">
      <Label className="practice__filter-label">Course</Label>
      <Input
        type="select"
        value={selectedCourseId}
        onChange={(e) => handleCourseFilterChange(e.target.value)}
      >
        <option value="">All Courses</option>
        {courseOptions.map((course) => (
          <option key={String(course.value)} value={String(course.value)}>
            {course.label}
          </option>
        ))}
      </Input>
    </div>
  );

  // Open a category: always fetch both its sub-categories and its direct modules.
  // Some categories can have modules directly under them even when sub-categories exist.
  const loadDirectModules = async (
    folderId: string | number,
    courseIdValue = selectedCourseId,
    page = 1
  ) => {
    const gen = page === 1 ? ++directModulesGenRef.current : directModulesGenRef.current;
    if (page === 1) {
      setDirectModulesLoading(true);
      setDirectModulesHasMore(false);
      setDirectModulesPage(1);
    } else {
      if (directModulesLoadingMoreRef.current) return;
      directModulesLoadingMoreRef.current = true;
      setDirectModulesLoadingMore(true);
    }
    try {
      const result = await getPracticeModules(folderId, courseIdValue || undefined, page);
      if (gen !== directModulesGenRef.current) return;
      setDirectModules((prev) =>
        page === 1 ? result.modules : mergeModules(prev, result.modules)
      );
      setDirectModulesPage(result.page);
      setDirectModulesHasMore(result.hasMore);
      return result;
    } catch (e) {
      console.error(e);
      if (gen === directModulesGenRef.current) toast.error('Failed to load modules.');
      return null;
    } finally {
      if (page === 1) {
        if (gen === directModulesGenRef.current) setDirectModulesLoading(false);
      } else {
        directModulesLoadingMoreRef.current = false;
        if (gen === directModulesGenRef.current) setDirectModulesLoadingMore(false);
      }
    }
  };

  const openCategory = async (cat: Practice) => {
    setOpeningId(cat.id);
    setDirectModules([]);
    setDirectModulesHasMore(false);
    try {
      const [subs, directPage] = await Promise.all([
        getSubCategories(cat.id, selectedCourseId || undefined),
        loadDirectModules(cat.id),
      ]);
      if (subs.length) {
        setSubCategories(subs);
        setView({ name: 'subcategories', category: cat });
      } else if (directPage) {
        setModules(directPage.modules);
        setModulesPage(directPage.page);
        setModulesHasMore(directPage.hasMore);
        setView({ name: 'modules', practice: cat });
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load sub-categories.');
    } finally {
      setOpeningId(null);
    }
  };

  React.useEffect(() => {
    const el = directModulesSentinelRef.current;
    if (!el || view.name !== 'subcategories' || !directModulesHasMore || directModulesLoading) return;
    const folderId = view.category.id;
    const courseIdValue = selectedCourseId;
    const nextPage = directModulesPage + 1;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadDirectModules(folderId, courseIdValue, nextPage);
        }
      },
      { root: null, rootMargin: '240px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [view, selectedCourseId, directModulesHasMore, directModulesLoading, directModulesPage]);

  // ---- create folder modal helpers ----
  const resetModal = () => {
    setName('');
    setDescription('');
    setTaggedCourses([]);
    setTaggedLevels([]);
    setPracticeType('');
  };

  const openAddCategory = () => {
    setCreateCtx({ parentFolderId: null, basePath: '/practice', label: 'Category' });
    setModalOpen(true);
  };

  const openAddSubCategory = (category: Practice) => {
    setCreateCtx({
      parentFolderId: Number(category.folderId ?? category.id) || null,
      basePath: category.folderPath || '/practice',
      label: 'Sub-category',
    });
    setModalOpen(true);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Name is required.');
      return;
    }
    setSaving(true);
    try {
      const created = await createPractice({
        name: name.trim(),
        description: description.trim(),
        courseIds: taggedCourses.map((o) => o.value),
        levels: taggedLevels.map((o) => o.value),
        practiceType,
        createdBy,
        parentFolderId: createCtx.parentFolderId,
        folderPath: `${createCtx.basePath}/${name.trim()}`,
      });
      toast.success(`${createCtx.label} created.`);
      setModalOpen(false);
      resetModal();
      if (createCtx.parentFolderId == null) setCategories((prev) => [created, ...prev]);
      else setSubCategories((prev) => [created, ...prev]);
    } catch (e) {
      console.error(e);
      toast.error(`Failed to create ${createCtx.label.toLowerCase()}.`);
    } finally {
      setSaving(false);
    }
  };

  const loadEditorVariants = async (
    moduleId: string | number,
    pitchTempo?: PitchTempoOption | null
  ): Promise<EditorVariant[]> => {
    const list = await getModuleVariants(moduleId, pitchTempo);
    return Promise.all(
      list.map(async (v) => {
        let audioUrl: string | undefined;
        if (v.recordingPath) {
          try {
            audioUrl = await getRecordingUrl(v.recordingPath);
          } catch (e) {
            console.error('Failed to fetch recording URL:', e);
          }
        }
        return { ...v, audioUrl };
      })
    );
  };

  // Open a module: load all its variants (resources) + their streaming audio.
  const openModule = async (
    practice: Practice,
    category: Practice | undefined,
    m: PracticeModule,
    returnView?: ReturnView
  ) => {
    setOpeningId(m.id);
    let variants: EditorVariant[] = [];
    try {
      variants = await loadEditorVariants(m.id);
    } catch (e) {
      console.error('Failed to fetch module variants:', e);
    }
    setOpeningId(null);

    // Prefer a variant that already has bars; else the first.
    const active =
      variants.find((v) => v.pitchMap.bars_data?.length) ?? variants[0] ?? null;
    setView({
      name: 'editor',
      practice,
      category,
      moduleId: m.id,
      title: m.name,
      isAIRecording: m.isEnabledForAI,
      aiVerified: !!m.aiVerified,
      variants,
      activeVariantId: active ? active.id : null,
      pitchTempoOptions: m.pitchTempoOptions ?? [],
      selectedPitchTempo: null,
      loadingPitchTempo: false,
      returnView,
    });
  };

  // ---- folder card grid (shared by categories + sub-categories) ----
  const renderFolderGrid = (items: Practice[], onOpen: (p: Practice) => void) => (
    <div className="practice__grid">
      {items.map((p) => (
        <button
          key={p.id}
          className="practice-card"
          onClick={() => onOpen(p)}
          disabled={openingId === p.id}
        >
          <div className="practice-card__title">
            <FiFolder className="me-2" />
            {p.name}
            {openingId === p.id && <Spinner size="sm" className="ms-2" />}
          </div>
          {p.description && <div className="practice-card__desc">{p.description}</div>}
          <div className="practice-card__meta">
            {p.moduleCount != null && (
              <span className="practice-card__chip">{p.moduleCount} items</span>
            )}
          </div>
        </button>
      ))}
    </div>
  );

  const renderModuleGrid = (
    items: PracticeModule[],
    practice: Practice,
    category?: Practice,
    returnView?: ReturnView
  ) => (
    <div className="practice__grid">
      {items.map((m) => (
        <button
          key={m.id}
          className={[
            'practice-card',
            m.isEnabledForAI
              ? m.aiVerified
                ? 'practice-card--verified'
                : 'practice-card--not-verified'
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={() => openModule(practice, category, m, returnView)}
          disabled={openingId === m.id}
          title="Open pitch map"
        >
          {m.isEnabledForAI && (
            <span
              className={`practice-card__status practice-card__status--${
                m.aiVerified ? 'verified' : 'not-verified'
              }`}
            >
              {m.aiVerified ? 'Verified' : 'Not Verified'}
            </span>
          )}
          <div className="practice-card__title">
            {m.name}
            {openingId === m.id && <Spinner size="sm" className="ms-2" />}
          </div>
          {m.description && <div className="practice-card__desc">{m.description}</div>}
          <div className="practice-card__meta">
            {m.moduleType && <span className="practice-card__chip">{m.moduleType}</span>}
            {m.pitch && <span className="practice-card__chip">Pitch {m.pitch}</span>}
            {m.tempo !== '' && m.tempo != null && (
              <span className="practice-card__chip">{m.tempo} BPM</span>
            )}
            {m.isEnabledForAI && (
              <span className="practice-card__chip practice-card__chip--ai-label">AI</span>
            )}
            {m.pitchMap?.bars_data?.length ? (
              <span className="practice-card__chip practice-card__chip--ai">
                {m.pitchMap.bars_data.length} bars
              </span>
            ) : null}
          </div>
        </button>
      ))}
    </div>
  );

  // ---- editor ----
  if (view.name === 'editor') {
    const v = view; // narrow
    const back = () =>
      setView(v.returnView ?? { name: 'modules', practice: v.practice, category: v.category });
    const active =
      v.variants.find((x) => String(x.id) === String(v.activeVariantId)) ?? v.variants[0] ?? null;

    const pitchMap: PitchMap = active
      ? { ...active.pitchMap, moduleId: v.moduleId, audioUrl: active.audioUrl }
      : { bars_data: [], moduleId: v.moduleId };

    const saveTarget = active
      ? { aiRecordingType: active.aiRecordingType, pitchTempo: v.selectedPitchTempo }
      : undefined;

    const variantOptions = v.variants.map((x) => ({ id: x.id, label: variantLabel(x) }));
    const pitchTempoOptions = v.pitchTempoOptions;
    const pitchTempoSelectValue = v.selectedPitchTempo
      ? pitchTempoValue(v.selectedPitchTempo)
      : '';
    const selectPitchTempo = async (value: string) => {
      const selected =
        pitchTempoOptions.find((option) => pitchTempoValue(option) === value) ?? null;
      setView({ ...v, selectedPitchTempo: selected, loadingPitchTempo: true });
      try {
        const nextVariants = await loadEditorVariants(v.moduleId, selected);
        const nextActive =
          nextVariants.find(
            (variant) =>
              active &&
              variant.aiRecordingType === active.aiRecordingType &&
              variant.recordingType === active.recordingType
          ) ??
          nextVariants.find((variant) => variant.pitchMap.bars_data?.length) ??
          nextVariants[0] ??
          null;
        setView({
          ...v,
          variants: nextVariants,
          activeVariantId: nextActive ? nextActive.id : null,
          selectedPitchTempo: selected,
          loadingPitchTempo: false,
        });
      } catch (e) {
        console.error('Failed to fetch selected pitch/tempo bars metadata:', e);
        toast.error('Failed to load bars metadata for the selected pitch and tempo.');
        setView({ ...v, loadingPitchTempo: false });
      }
    };

    return (
      <div className="practice">
        <button className="practice__back" onClick={back}>
          <FiArrowLeft /> Back to {v.practice.name}
        </button>
        <div className="practice__head">
          <h5 className="practice__name">{v.title}</h5>
        </div>
        {pitchTempoOptions.length > 0 && (
          <div className="practice__selector">
            <Label className="practice__selector-label">Pitch / Tempo</Label>
            <Input
              type="select"
              value={pitchTempoSelectValue}
              onChange={(e) => selectPitchTempo(e.target.value)}
              disabled={v.loadingPitchTempo}
            >
              <option value="">Default bars metadata</option>
              {pitchTempoOptions.map((option) => (
                <option key={pitchTempoValue(option)} value={pitchTempoValue(option)}>
                  {pitchTempoLabel(option)}
                </option>
              ))}
            </Input>
            {v.loadingPitchTempo && <Spinner size="sm" color="primary" />}
          </div>
        )}
        <PitchMapEditor
          key={`${active ? active.id : 'empty'}-${pitchTempoSelectValue || 'default'}`}
          pitchMap={pitchMap}
          audioFileName={audioDownloadName(v.title, active)}
          onSaved={back}
          isAIRecording={v.isAIRecording}
          isVerified={v.aiVerified}
          saveTarget={saveTarget}
          variants={variantOptions}
          activeVariantId={v.activeVariantId}
          onSelectVariant={(id) => setView({ ...v, activeVariantId: id })}
          resourceId={active?.id}
          isResourceVerified={!!active?.isAiPracticeVerified}
          onResourceVerificationChanged={(isVerified) =>
            setView({
              ...v,
              variants: v.variants.map((variant) =>
                active && String(variant.id) === String(active.id)
                  ? { ...variant, isAiPracticeVerified: isVerified }
                  : variant
              ),
            })
          }
        />
      </div>
    );
  }

  // ---- module upload ----
  if (view.name === 'upload') {
    const back = () =>
      setView({ name: 'modules', practice: view.practice, category: view.category });
    return (
      <ModuleUpload
        folderId={Number(view.practice.folderId ?? view.practice.id) || 0}
        folderPath={view.practice.folderPath ?? `/practice/${view.practice.name}`}
        practiceName={view.practice.name}
        createdBy={createdBy}
        onBack={back}
        onDone={back}
      />
    );
  }

  // ---- modules of a folder ----
  if (view.name === 'modules') {
    const p = view.practice;
    const back = () =>
      view.category
        ? setView({ name: 'subcategories', category: view.category })
        : setView({ name: 'categories' });
    return (
      <div className="practice">
        <button className="practice__back" onClick={back}>
          <FiArrowLeft /> {view.category ? `Back to ${view.category.name}` : 'All Categories'}
        </button>
        <div className="practice__head">
          <div>
            <h5 className="practice__name">{p.name}</h5>
            {p.description && <p className="practice__desc">{p.description}</p>}
          </div>
          <div className="practice__head-actions">
            {renderCourseFilter()}
            <Button
              color="primary"
              onClick={() => setView({ name: 'upload', practice: p, category: view.category })}
            >
              <FiPlus /> Add Module
            </Button>
          </div>
        </div>

        {modulesLoading ? (
          <div className="practice__loading">
            <Spinner color="primary" /> <span>Loading modules…</span>
          </div>
        ) : modules.length === 0 ? (
          <div className="practice__empty">
            <FiMusic size={28} />
            <p>No modules yet. Use “Add Module” to upload practice material.</p>
          </div>
        ) : (
          <>
            {renderModuleGrid(modules, p, view.category)}
            {modulesHasMore && (
              <div ref={modulesSentinelRef} className="practice__load-more">
                {modulesLoadingMore && (
                  <>
                    <Spinner size="sm" color="primary" /> <span>Loading more…</span>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ---- sub-categories of a category ----
  if (view.name === 'subcategories') {
    const cat = view.category;
    return (
      <div className="practice">
        <button className="practice__back" onClick={() => setView({ name: 'categories' })}>
          <FiArrowLeft /> All Categories
        </button>
        <div className="practice__head">
          <h5 className="practice__name">{cat.name}</h5>
          <div className="practice__head-actions">
            {renderCourseFilter()}
            <Button color="primary" onClick={() => openAddSubCategory(cat)}>
              <FiPlus /> Add Sub-category
            </Button>
          </div>
        </div>
        {subCategories.length === 0 ? (
          <div className="practice__empty">
            <FiFolder size={28} />
            <p>No sub-categories yet.</p>
          </div>
        ) : (
          renderFolderGrid(subCategories, (sub) =>
            setView({ name: 'modules', practice: sub, category: cat })
          )
        )}
        {directModulesLoading ? (
          <div className="practice__loading">
            <Spinner color="primary" /> <span>Loading direct modules…</span>
          </div>
        ) : directModules.length ? (
          <>
            <div className="practice__head practice__head--section">
              <h5 className="practice__name">Modules in {cat.name}</h5>
            </div>
            {renderModuleGrid(directModules, cat, undefined, {
              name: 'subcategories',
              category: cat,
            })}
            {directModulesHasMore && (
              <div ref={directModulesSentinelRef} className="practice__load-more">
                {directModulesLoadingMore && (
                  <>
                    <Spinner size="sm" color="primary" /> <span>Loading more…</span>
                  </>
                )}
              </div>
            )}
          </>
        ) : null}
        {renderModal()}
      </div>
    );
  }

  // ---- categories (landing) ----
  return (
    <div className="practice">
      <div className="practice__head">
        <div>
          <p className="practice__subtitle">
            Browse practice categories, sub-categories, and modules.
          </p>
          {renderCourseFilter()}
        </div>
        <Button color="primary" onClick={openAddCategory}>
          <FiPlus /> Add Category
        </Button>
      </div>

      {loading ? (
        <div className="practice__loading">
          <Spinner color="primary" /> <span>Loading categories…</span>
        </div>
      ) : categories.length === 0 ? (
        <div className="practice__empty">
          <p>No categories yet. Create your first one.</p>
        </div>
      ) : (
        renderFolderGrid(categories, openCategory)
      )}

      {renderModal()}
    </div>
  );

  // ---- shared create modal ----
  function renderModal() {
    return (
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)}>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>Add {createCtx.label}</ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={`${createCtx.label} name`} />
          </FormGroup>
          <FormGroup>
            <Label>Description</Label>
            <Input
              type="textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
            />
          </FormGroup>
          <FormGroup>
            <Label>Tag to Courses</Label>
            <Select
              isMulti
              options={courseOptions}
              value={taggedCourses}
              onChange={(vals) => setTaggedCourses(vals as any[])}
              placeholder="Select one or more courses…"
            />
          </FormGroup>
          <FormGroup>
            <Label>Level</Label>
            <Select
              isMulti
              options={levelOptions}
              value={taggedLevels}
              onChange={(vals) => setTaggedLevels(vals as any[])}
              placeholder="Select one or more levels…"
            />
          </FormGroup>
          <FormGroup>
            <Label>Practice Type</Label>
            <Input type="select" value={practiceType} onChange={(e) => setPracticeType(e.target.value)}>
              <option value="">Select…</option>
              {PRACTICE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Created By</Label>
            <Input value={createdBy || '(current user)'} disabled />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleCreate} disabled={saving}>
            {saving ? <Spinner size="sm" /> : 'Save'}
          </Button>
          <Button color="secondary" onClick={() => setModalOpen(false)} disabled={saving}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
};

export default PracticeMaterial;
